/**
 * SmsCombinedPanel — Shared component used across all dashboards.
 * Shows sent (OUT) and received (IN) SMS messages with search, direction filter,
 * and per-message delete for authorized roles (super_admin, admin, controller, asst_controller).
 */

import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

type Direction = "all" | "sent" | "received";

function TrashIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

export default function SmsCombinedPanel() {
  const { user } = useAuth();
  const canDelete = ["super_admin", "admin", "controller", "asst_controller"].includes(user?.role ?? "");

  const utils = trpc.useUtils();
  const { data: messages = [], isLoading } = trpc.clientNotifications.smsCombined.useQuery(
    { limit: 100 },
    { refetchInterval: 30000 }
  );

  const deleteSentMutation = trpc.clientNotifications.deleteMessage.useMutation({
    onSuccess: () => {
      toast.success("Message removed from WorkCenter");
      utils.clientNotifications.smsCombined.invalidate();
    },
    onError: (err) => toast.error(`Delete failed: ${err.message}`),
  });

  const deleteReceivedMutation = trpc.clientNotifications.deleteReceived.useMutation({
    onSuccess: () => {
      toast.success("Message removed from WorkCenter");
      utils.clientNotifications.smsCombined.invalidate();
    },
    onError: (err) => toast.error(`Delete failed: ${err.message}`),
  });

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Direction>("all");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return messages.filter((msg) => {
      const matchesDir =
        filter === "all" ||
        (filter === "sent" && msg.direction === "sent") ||
        (filter === "received" && msg.direction === "received");
      if (!matchesDir) return false;
      if (!q) return true;
      return (
        msg.contactName?.toLowerCase().includes(q) ||
        msg.body?.toLowerCase().includes(q) ||
        msg.phoneOrEmail?.toLowerCase().includes(q) ||
        msg.senderName?.toLowerCase().includes(q)
      );
    });
  }, [messages, search, filter]);

  const handleDelete = (msg: (typeof messages)[0]) => {
    if (!window.confirm("Remove this message from WorkCenter? (Twilio/Textellent records are not affected.)")) return;
    const numId = parseInt(msg.id.replace(/^(sent|recv)-/, ""), 10);
    if (msg.direction === "sent") {
      deleteSentMutation.mutate({ id: numId });
    } else {
      deleteReceivedMutation.mutate({ id: numId });
    }
  };

  return (
    <div className="apx-card rounded-xl p-5 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3
          className="text-base font-semibold text-[oklch(0.08_0.01_240)]"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          SMS MESSAGES SENT / RECEIVED
        </h3>
        <span className="text-xs text-[oklch(0.30_0.03_240)]">
          {filtered.length} of {messages.length}
        </span>
      </div>

      {/* Search + Filter row */}
      <div className="flex items-center gap-2 mb-4">
        {/* Search input */}
        <div className="relative flex-1">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[oklch(0.40_0.03_240)] pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by name, message, phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg outline-none transition-all"
            style={{
              background: "oklch(0.93 0.008 240)",
              border: "1.5px solid oklch(0.82 0.01 240)",
              color: "oklch(0.08 0.01 240)",
            }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[oklch(0.40_0.03_240)] hover:text-[oklch(0.08_0.01_240)]"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Direction filter pills */}
        <div className="flex items-center gap-1 shrink-0">
          {(["all", "sent", "received"] as Direction[]).map((d) => (
            <button
              key={d}
              onClick={() => setFilter(d)}
              className="px-2.5 py-1 rounded-md text-xs font-semibold transition-all"
              style={
                filter === d
                  ? {
                      background: d === "received" ? "oklch(0.72 0.14 75)" : "oklch(0.25 0.04 240)",
                      color: d === "received" ? "oklch(0.12 0.04 240)" : "oklch(0.95 0.006 240)",
                    }
                  : {
                      background: "oklch(0.93 0.008 240)",
                      color: "oklch(0.30 0.03 240)",
                      border: "1.5px solid oklch(0.82 0.01 240)",
                    }
              }
            >
              {d === "all" ? "All" : d === "sent" ? "OUT" : "IN"}
            </button>
          ))}
        </div>
      </div>

      {/* Message list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <span className="text-sm text-[oklch(0.30_0.03_240)] animate-pulse">Loading…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 gap-2">
          <p className="text-sm text-[oklch(0.30_0.03_240)]">
            {messages.length === 0 ? "No SMS messages yet." : "No messages match your search."}
          </p>
          {search && (
            <button
              onClick={() => setSearch("")}
              className="text-xs underline text-[oklch(0.40_0.03_240)] hover:text-[oklch(0.08_0.01_240)]"
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2 flex-1 min-h-0 overflow-y-auto pr-1">
          {filtered.map((msg) => (
            <div
              key={msg.id}
              className="flex items-start gap-3 px-3 py-3 rounded-lg"
              style={{
                background: msg.direction === "received" ? "oklch(0.95 0.012 85)" : "oklch(0.93 0.008 240)",
              }}
            >
              <div
                className="shrink-0 w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold mt-0.5"
                style={{
                  background: msg.direction === "received" ? "oklch(0.72 0.14 75)" : "oklch(0.25 0.04 240)",
                  color: msg.direction === "received" ? "oklch(0.12 0.04 240)" : "oklch(0.95 0.006 240)",
                }}
              >
                {msg.direction === "received" ? "IN" : "OUT"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-[oklch(0.18_0.04_240)] truncate">
                    {msg.contactName}
                  </span>
                </div>
                <p className="text-sm text-[oklch(0.08_0.01_240)] mt-0.5 line-clamp-2">{msg.body}</p>
                <p className="text-sm text-[oklch(0.30_0.03_240)] mt-1">
                  {msg.phoneOrEmail}
                  {msg.senderName ? ` · sent by ${msg.senderName}` : ""} ·{" "}
                  {new Date(msg.timestamp).toLocaleString()}
                </p>
              </div>
              {/* Delete button — always visible for authorized roles */}
              {canDelete && (
                <button
                  onClick={() => handleDelete(msg)}
                  title="Remove from WorkCenter"
                  className="shrink-0 mt-0.5 p-1.5 rounded-md transition-colors hover:bg-red-50"
                  style={{ color: "oklch(0.55 0.18 25)", border: "none", background: "transparent", cursor: "pointer" }}
                >
                  <TrashIcon />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
